import { useEffect, useRef } from "react";
import {
  type Chevron,
  type DayButton,
  DayPicker,
  type DayPickerProps,
  Dropdown,
  type Locale,
  type NextMonthButton,
  type PreviousMonthButton,
  type PropsBase,
  type PropsMulti,
  type PropsMultiRequired,
  type PropsRange,
  type PropsRangeRequired,
  type PropsSingle,
  type PropsSingleRequired,
} from "react-day-picker";
import { enGB } from "react-day-picker/locale";
import styled from "@emotion/styled";

import UIcon from "lib/icons/untitled_ui/UIcon";
import { capitalize } from "lib/utils";
import Button from "../button/Button";

// Need to remove the mode: undefined option from the base props and I couldn't find
// a better way to do this without the conditional types collapsing
export type CalendarProps = PropsBase &
  (
    | PropsSingle
    | PropsSingleRequired
    | PropsMulti
    | PropsMultiRequired
    | PropsRange
    | PropsRangeRequired
  );

const Calendar = ({ locale = enGB, ...props }: CalendarProps) => {
  return (
    <StyledDayPicker
      captionLayout="dropdown-years"
      navLayout="after"
      showOutsideDays
      weekStartsOn={1}
      locale={locale}
      timeZone="UTC"
      components={{
        Chevron: ({ orientation, ...props }: React.ComponentProps<typeof Chevron>) => (
          <UIcon icon={`chevron${capitalize(orientation ?? "down")}`} {...props} size={16} />
        ),
        DayButton: ({ ...props }: React.ComponentProps<typeof DayButton>) => (
          <CalendarDayButton locale={locale} {...props} />
        ),
        YearsDropdown: ({ ...props }: React.ComponentProps<typeof Dropdown>) => (
          <Dropdown name="Year Dropdown" {...props} />
        ),
        PreviousMonthButton: ({
          children: _children, // We want the arrow icons to be done at this level so the buttons adapt the padding as needed
          ...props
        }: React.ComponentProps<typeof PreviousMonthButton>) => (
          <Button
            buttonType="primary"
            buttonStyle="inverse"
            icon="chevronLeft"
            {...props}
            disabled={props.tabIndex === -1} // Disabled isn't passed down for some reason
          />
        ),
        NextMonthButton: ({
          children: _children, // We want the arrow icons to be done at this level so the buttons adapt the padding as needed
          ...props
        }: React.ComponentProps<typeof NextMonthButton>) => (
          <Button
            buttonType="primary"
            buttonStyle="inverse"
            icon="chevronRight"
            {...props}
            disabled={props.tabIndex === -1} // Disabled isn't passed down for some reason
          />
        ),
      }}
      {...props}
    />
  );
};

const CalendarDayButton = ({
  day,
  modifiers,
  locale,
  ...props
}: React.ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  return (
    <CalendarDayButtonStyled
      ref={ref}
      buttonType="primary"
      buttonStyle="inverse"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      data-outside={modifiers.outside}
      {...props}
    />
  );
};

const CalendarDayButtonStyled = styled(Button)(({ theme }) => ({
  width: 32,
  height: 32,
  padding: 0,
  transition: "none",
  "&[data-selected-single=true], &[data-range-start=true], &[data-range-end=true]": {
    backgroundColor: theme.colors.sysPrimaryDefault,
    color: theme.colors.sysTextWhite,
  },

  "&[data-range-middle=true]": {
    backgroundColor: "transparent",
    color: theme.colors.sysTextDefault,
  },
}));

const StyledDayPicker = styled(DayPicker)<DayPickerProps>(({ theme }) => ({
  // Loosely based on a combination of shadcn and the default styles from
  // https://github.com/gpbl/react-day-picker/blob/38e1b27c13e4c11b7f2e47668803c981d074ddef/src/style.css
  // However, I've stripped out many of the styling which was occurring since we know how we want it to look
  // meaning you might find some styles need to be added back in if a particular feature is used

  width: "fit-content",
  backgroundColor: theme.colors.sysBackgroundDefault,
  color: theme.colors.sysTextDefault,

  "& .rdp-months": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    position: "relative",
    gap: 16,
  },

  // Container for just nav buttons
  "& .rdp-nav": {
    display: "flex",
    alignItems: "center",
    position: "absolute",
    insetBlockStart: 0,
    insetInlineEnd: 0,
  },

  // Container for month and year
  "& .rdp-month_caption": {
    display: "flex",
    alignItems: "center",
    height: 32,
    paddingLeft: 6,
  },
  // Dropdowns for month and year selection
  "& .rdp-dropdowns": {
    display: "flex",
    alignItems: "center",
    gap: 8,
    position: "relative",
  },
  "& .rdp-dropdown_root": {
    display: "flex",
    alignItems: "center",
    position: "relative",
    "&[data-disabled=true] .rdp-chevron": {
      display: "none",
    },
  },
  "& .rdp-dropdown": {
    width: "100%",
    position: "absolute",
    cursor: "pointer",
    opacity: 0,
    userSelect: "none",
  },
  "& .rdp-caption_label": {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  // Calendar actual
  "& .rdp-month_grid": {
    borderSpacing: "0px 4px",
  },
  "& .rdp-weekdays": {
    height: 32,
  },
  "& .rdp-day": {
    padding: 0,
    width: 32,
    height: 32,
  },

  // Range styles
  "& .rdp-range_start, & .rdp-range_middle, & .rdp-range_end": {
    backgroundColor: theme.colors.sysPrimarySubtleAlt,
  },
  "& .rdp-range_start": {
    borderTopLeftRadius: "4px",
    borderBottomLeftRadius: "4px",
  },
  "& .rdp-range_end": {
    borderTopRightRadius: "4px",
    borderBottomRightRadius: "4px",
  },

  // Text styling
  "& .rdp-weekday, & .rdp-day_button[data-outside=true]": {
    fontWeight: 400,
    fontSize: 14,
    color: theme.colors.sysTextInactive,
  },
}));

export default Calendar;
